import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Popper from "@mui/material/Popper";
import BulkOrderItemsTable from "./BulkOrderItemsTable";

const clientTypeLabels = {
  0: "Client Final",
  1: "Client Marmist",
};

const CustomPopper = ({ darkMode, ...props }) => (
  <Popper
    {...props}
    modifiers={[
      {
        name: "offset",
        options: {
          offset: [0, 4],
        },
      },
    ]}
    sx={{
      backgroundColor: "transparent",
      boxShadow: "none",
      border: "none",
      marginTop: "0px",
      padding: "0px",
      "& .MuiAutocomplete-paper": {
        backgroundColor: darkMode ? "#1f2937" : "#f9fafb",
        color: darkMode ? "#fff" : "#111",
        boxShadow: darkMode ? "none" : "0 6px 20px rgba(0,0,0,0.15)",
        borderRadius: "8px",
        border: darkMode ? "none" : "1px solid #d1d5db",
        margin: 0,
        padding: 0,
      },
      "& .MuiAutocomplete-listbox": {
        padding: 0,
        margin: 0,
      },
      "& .MuiAutocomplete-option": {
        backgroundColor: darkMode ? "#1f2937" : "#f9fafb",
        color: darkMode ? "#fff" : "#111",
        minHeight: "44px",
        fontWeight: 500,
        borderBottom: darkMode ? "1px solid #374151" : "1px solid #e5e7eb",
        '&[aria-selected="true"]': {
          backgroundColor: darkMode ? "#374151" : "#e2e8f0",
        },
        "&:hover": {
          backgroundColor: darkMode ? "#374151" : "#f1f5f9",
        },
      },
    }}
  />
);

const NewOrderForm = ({
  orderNumber,
  customerId,
  setCustomerId,
  customers,
  street,
  setStreet,
  orderItems,
  setIsProductModalOpen,
  // setIsDrawingModalOpen,
  removeOrderItem,
  darkMode,
  handleSubmit,
  loading,
  isProductModalOpen,
  products,
  setOrderItems,
  discountPercentage,
  setDiscountPercentage,
  preferentialPrice,
  setPreferentialPrice,
  isAdvanceOrder,
  setIsAdvanceOrder,
  isGranitOnline,
  setIsGranitOnline,
  customerSource,
  setCustomerSource
}) => {
  const [cuLivrare, setCuLivrare] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [lastModifiedField, setLastModifiedField] = useState(null);
  const [customerType, setCustomerType] = useState(null);
  const [tva, setTva] = useState(0);
  const [totalWithTVA, setTotalWithTVA] = useState(0);

  useEffect(() => {
    const subtotal = orderItems.reduce((acc, item) => {
      const mp = parseFloat(item.quantityMP);
      const ml = parseFloat(item.quantityML);
      const buc = parseFloat(item.quantityBUC);

      const product = products.find((p) => p.id === item.productId);
      if (!product) return acc;

      const price = customerType === 1 ? product.priceMarmurist : product.price;

      let itemTotal = 0;

      if (ml && ml > 0) {
        // ML prior -> ha van ML, csak ML
        itemTotal = price * ml;
      } else if (mp && mp > 0) {
        // MP csak ha nincs ML
        itemTotal = price * mp;
      } else if (buc && buc > 0) {
        // Buc csak ha nincs ML és MP
        itemTotal = price * buc;
      }

      return acc + itemTotal;
    }, 0);

    const calculatedTVA = parseFloat((subtotal * 0.21).toFixed(2));
    const totalBrut = parseFloat((subtotal + calculatedTVA).toFixed(2));

    setTotalPrice(parseFloat(subtotal.toFixed(2))); // nettó
    setTva(calculatedTVA); // TVA
    setTotalWithTVA(totalBrut); // bruttó
  }, [orderItems, products, customerType]);

  useEffect(() => {
    if (!totalPrice || totalPrice === 0) return;

    const brutto = totalPrice * 1.21;

    if (lastModifiedField === "preferentialPrice") {
      // Kedvezmény = bruttó - preferențial
      const discountedNet = preferentialPrice / 1.21;
      const discount = ((totalPrice - discountedNet) * 100) / totalPrice;
      setDiscountPercentage(parseFloat(discount.toFixed(2)));
    } else if (lastModifiedField === "discountPercentage") {
      // Nettó kedvezmény
      const discountedNet = totalPrice * (1 - discountPercentage / 100);
      const discountedGross = parseFloat((discountedNet * 1.21).toFixed(2));
      setPreferentialPrice(discountedGross);
    }
  }, [preferentialPrice, discountPercentage, totalPrice, lastModifiedField]);

  useEffect(() => {
    if (totalPrice > 0 && lastModifiedField === null) {
      setPreferentialPrice(parseFloat((totalPrice * 1.21).toFixed(2)));
    }
  }, [totalPrice, lastModifiedField]);

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {" "}
      {/* <label
        className={`block font-semibold ${
          darkMode ? "text-gray-300" : "text-gray-700"
        }`}
      >
        Nr. Comanda
      </label>
      <input
        type="text"
        value={orderNumber || ""}
        disabled
        className={`w-full p-2 border rounded-2xl ${
          darkMode
            ? "border-gray-600 bg-gray-800 text-white"
            : "border-gray-300 bg-white text-black"
        }`}
      /> */}

      {/* Tip Comandă: Avansos / Completă */}
      <div className="flex items-center gap-6 mt-2">
        {/* Bal oldal – Tip comandă */}
        <div className="flex items-center gap-4">
          <label className={`font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
            Tip comandă:
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="orderType"
              value="full"
              checked={!isAdvanceOrder}
              onChange={() => setIsAdvanceOrder(false)}
            />
            <span className={darkMode ? "text-white" : "text-black"}>Completă</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="orderType"
              value="advance"
              checked={isAdvanceOrder}
              onChange={() => setIsAdvanceOrder(true)}
            />
            <span className={darkMode ? "text-white" : "text-black"}>Avans</span>
          </label>
        </div>

        {/* Jobb oldal – Granit Online */}
        <label className="ml-36 flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isGranitOnline}
            onChange={(e) => setIsGranitOnline(e.target.checked)}
            className="w-4 h-4 cursor-pointer"
          />

          <span className="text-sm font-medium">
            Comandă Granit Online
          </span>
        </label>

      </div>

      {/* Sursa clientului */}
      <div className="flex items-center gap-4 mt-1">
        <label className={`font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
          Sursa clientului:
        </label>
        {[{value: 1, label: "Online"}, {value: 2, label: "Recomandare"}, {value: 3, label: "Recurent"}].map(opt => (
          <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="customerSource"
              value={opt.value}
              checked={customerSource === opt.value}
              onChange={() => setCustomerSource(opt.value)}
            />
            <span className={darkMode ? "text-white" : "text-black"}>{opt.label}</span>
          </label>
        ))}

      </div>

      <div className="flex items-end gap-4">
        {/* Client selector */}
        <div className={`flex flex-col ${cuLivrare ? "w-1/3" : "w-1/2"}`}>
          <label
            className={`font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"
              }`}
          >
            Client
          </label>
          {!isProductModalOpen && (
            <Autocomplete
              disablePortal
              options={customers}
              getOptionLabel={(customer) =>
                `${customer.name} - ${clientTypeLabels[customer.type] || "Necunoscut"
                }`
              }
              value={customers.find((c) => c.id === customerId) || null}
              onChange={(event, newValue) => {
                setCustomerId(newValue ? newValue.id : "");
                setCustomerType(newValue ? newValue.type : null);

                // 🔁 Reseteljük a kedvezményeket
                setPreferentialPrice(0);
                setDiscountPercentage(0);
                setLastModifiedField(null); // vagy "preferentialPrice" ha default legyen
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              PopperComponent={(props) => (
                <CustomPopper {...props} darkMode={darkMode} />
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Selectează clientul"
                  InputLabelProps={{
                    style: {
                      color: darkMode ? "#ccc" : "#333",
                      fontWeight: "600",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: {
                      backgroundColor: darkMode ? "#1f2937" : "#fff",
                      color: darkMode ? "#fff" : "#000",
                      borderRadius: "16px",
                      width: "100%",
                    },
                  }}
                />
              )}
            />
          )}
        </div>

        {/* Cu/Fără livrare button */}
        <div className={`flex flex-col ${cuLivrare ? "w-1/3" : "w-1/2"}`}>
          <label className="invisible">Toggle livrare</label>
          <Button
            variant="outlined"
            sx={{
              borderRadius: "30px",
              height: "48px",
              fontSize: "20px",
              fontWeight: "bold",
              color: darkMode ? "#fff" : "#000",
              borderColor: darkMode ? "#ccc" : "#999",
              width: "100%",
            }}
            onClick={() => setCuLivrare((prev) => !prev)}
          >
            {cuLivrare ? "Cu livrare" : "Fără livrare"}
          </Button>
        </div>

        {/* Strada input, only if cuLivrare is true */}
        {cuLivrare && (
          <div className="flex flex-col w-1/3">
            <label
              className={`font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"
                }`}
            >
              Strada
            </label>
            <input
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              className={`p-2 border rounded-2xl ${darkMode
                ? "border-gray-600 bg-gray-800 text-white"
                : "border-gray-300 bg-white text-black"
                }`}
            />
          </div>
        )}
      </div>
      <h3
        className={`text-lg font-semibold mt-4 ${darkMode ? "text-white" : "text-gray-900"
          }`}
      >
        Produse
      </h3>
      {/* <Button
        variant="contained"
        sx={{
          borderRadius: "30px",
          width: "100%",
          color: "white",
          height: "56px",
          fontSize: "16px",
          fontWeight: "bold",
          backgroundColor: darkMode ? "#1976d2" : "#1565c0",
        }}
        onClick={() => setIsProductModalOpen(true)}
      >
        + Adaugă produs
      </Button> */}
      <BulkOrderItemsTable
        products={products}
        orderItems={orderItems}
        setOrderItems={setOrderItems}
        darkMode={darkMode}
      />
      {/* 1. sor – Total / Preferențial / Discount */}
      <div className="grid grid-cols-3 gap-4 mt-6">
        <div>
          <label
            className={`block mb-1 font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"
              }`}
          >
            Total brut (TVA inclus, fără discount)
          </label>
          <input
            type="number"
            value={totalWithTVA}
            disabled
            className={`w-full p-2 rounded-xl ${darkMode ? "bg-gray-800 text-white" : "bg-gray-100 text-black"
              }`}
          />
        </div>

        <div>
          <label
            className={`block mb-1 font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"
              }`}
          >
            Preț Preferențial
          </label>
          <input
            type="number"
            value={preferentialPrice}
            onChange={(e) => {
              setPreferentialPrice(parseFloat(e.target.value));
              setLastModifiedField("preferentialPrice");
            }}
            className={`w-full p-2 border rounded-xl ${darkMode
              ? "bg-gray-800 border-gray-600 text-white"
              : "bg-white border-gray-300 text-black"
              }`}
          />
        </div>

        <div>
          <label
            className={`block mb-1 font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"
              }`}
          >
            Discount (%)
          </label>
          <input
            type="number"
            value={discountPercentage}
            onChange={(e) => {
              setDiscountPercentage(parseFloat(e.target.value));
              setLastModifiedField("discountPercentage");
            }}
            className={`w-full p-2 border rounded-xl ${darkMode
              ? "bg-gray-800 border-gray-600 text-white"
              : "bg-white border-gray-300 text-black"
              }`}
          />
        </div>
      </div>
      {/* 2. sor – Gombok egymás mellett */}
      <div className="mt-6 text-center">
        {/* <Button
          variant="contained"
          color="secondary"
          sx={{
            borderRadius: "30px",
            width: "50%",
            color: "white",
            height: "56px",
            fontSize: "16px",
            fontWeight: "bold",
          }}
          onClick={() => setIsDrawingModalOpen(true)}
        >
          Desenează schița
        </Button> */}

        <Button
          variant="contained"
          color="success"
          sx={{
            borderRadius: "30px",
            width: "50%",
            color: "white",
            height: "56px",
            fontSize: "16px",
            fontWeight: "bold",
            backgroundColor: darkMode ? "#2e7d32" : "#388e3c",
          }}
          type="submit"
          disabled={loading}
        >
          Salvează și Închide
        </Button>
      </div>
    </form>
  );
};

export default NewOrderForm;
